import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X } from '../../components/common/Icons';
import { COUNTRIES, searchCountries, getCountryByCode } from '../../utils/countries';
import CountryFlag from './CountryFlag';

const CountrySelector = ({ 
  value, 
  onChange, 
  placeholder = "Select country...",
  required = false,
  className = "",
  disabled = false,
  clearable = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCountries, setFilteredCountries] = useState(COUNTRIES);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const selectedCountry = getCountryByCode(value);

  useEffect(() => {
    setFilteredCountries(searchCountries(searchQuery));
  }, [searchQuery]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the button and the dropdown
      const isClickOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
      const isClickOutsideDropdown = !event.target.closest('[data-country-dropdown]');
      
      if (isClickOutsideButton && isClickOutsideDropdown) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle scroll events to reposition dropdown smoothly
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        
        // Debounce the position recalculation for smooth scrolling
        scrollTimeoutRef.current = setTimeout(() => {
          calculateDropdownPosition();
        }, 16); // ~60fps for smooth updates
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true); // Use capture for all scrollable elements
      window.addEventListener('resize', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [isOpen]);

  const handleCountrySelect = (country) => {
    onChange(country.code);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
  };

  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 320; // max-h-80 = 320px
      
      // Check if there's enough space below the button
      const spaceBelow = viewportHeight - rect.bottom;
      const shouldOpenUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
      
      // Use viewport-relative positioning since we're using createPortal
      setDropdownPosition({
        top: shouldOpenUpward 
          ? rect.top - dropdownHeight - 8
          : rect.bottom + 8,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
        width: rect.width
      });
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      calculateDropdownPosition();
    }
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus search input when opening
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Value Display */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`w-full px-3 py-2 text-left text-sm bg-zinc-900/70 border rounded-lg transition-colors flex items-center justify-between ${
          disabled
            ? 'border-white/10 text-gray-500 cursor-not-allowed'
            : isOpen
              ? 'border-[#31C590]/60 ring-2 ring-[#31C590]/20'
              : 'border-white/10 hover:border-white/20'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-2.5 flex-1 min-w-0">
          {selectedCountry ? (
            <>
              <CountryFlag
                countryCode={selectedCountry.code}
                size="w-5 h-3.5"
                alt={selectedCountry.name}
              />
              <span className="text-white truncate">{selectedCountry.name}</span>
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {clearable && selectedCountry && !disabled && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleClear(e);
              }}
              className="p-1 hover:bg-white/10 rounded transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear(e);
                }
              }}
              aria-label="Clear selection"
            >
              <X className="w-4 h-4 text-gray-400 hover:text-white" />
            </div>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {/* Dropdown Portal */}
      {isOpen && createPortal(
        <div 
          data-country-dropdown
          className="fixed bg-[#12151e] border border-white/10 rounded-lg shadow-2xl max-h-80 overflow-hidden transition-all duration-200 ease-out animate-in fade-in-0 zoom-in-95"
          style={{ 
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 1000,
            transformOrigin: 'top center'
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search countries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-zinc-900/70 border border-white/10 rounded-md text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#31C590] focus:border-[#31C590]"
              />
            </div>
          </div>

          {/* Countries List */}
          <div className="overflow-y-auto max-h-64">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full px-4 py-2.5 text-left hover:bg-white/[0.06] flex items-center space-x-3 transition-colors ${
                    value === country.code
                      ? 'bg-[#31C590]/15 text-[#23F8B5]'
                      : 'text-white'
                  }`}
                  role="option"
                  aria-selected={value === country.code}
                >
                  <CountryFlag 
                    countryCode={country.code}
                    size="w-6 h-4"
                    alt={country.name}
                  />
                  <span className="flex-1">{country.name}</span>
                  <span className="text-xs text-gray-400 font-mono">{country.code}</span>
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                No countries found matching "{searchQuery}"
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Hidden input for form validation */}
      {required && (
        <input
          type="hidden"
          value={value || ''}
          required={required}
          onChange={() => {}} // Controlled by the component
        />
      )}
    </div>
  );
};

export default CountrySelector;